import { AudioManager } from "./audio/audio.ts";
import { App } from "./app.ts";
import { JUICE, LEVEL_DIFFICULTY, PLAYER } from "./config.ts";
import { GameLoop } from "./core/loop.ts";
import { hashSeed } from "./core/rng.ts";
import { buildLevel1 } from "./content/level1.ts";
import { buildLevel2 } from "./content/level2.ts";
import { buildLevel3 } from "./content/level3.ts";
import { buildLevel4 } from "./content/level4.ts";
import { buildLevel5 } from "./content/level5.ts";
import type { Level } from "./content/levelTypes.ts";
import { EN_METEOR } from "./entities/enemy.ts";
import { Input } from "./input/input.ts";
import { Renderer } from "./systems/render.ts";
import { Scoring } from "./systems/scoring.ts";
import { TimelineRunner } from "./systems/spawn.ts";
import { UI } from "./ui/ui.ts";
import { World } from "./world.ts";

type State = "menu" | "playing" | "outro" | "paused" | "levelclear" | "results";

const UNLOCK_STORAGE_KEY = "flowstate.unlockedLevel";
/** 0-based level index where the sword comes online. */
const SWORD_UNLOCK_LEVEL = 2;
/** Seconds the stage keeps running after the timeline ends, so hazards drift
 * off and enemies leave before the clear banner — no more freeze-frame end. */
const OUTRO_TIME = 2.6;
/** Longer wind-down after the dragon falls: the explosion fades and the empty
 * arena sits quiet for a few seconds so the victory can breathe before the
 * results appear. */
const BOSS_OUTRO_TIME = 6.5;

/**
 * Top-level orchestrator: owns the fixed-timestep loop and the state machine
 * (menu → playing → levelclear → results), and wires the World, Renderer,
 * timeline, scoring, audio, input, and UI together.
 */
export class Game {
  private state: State = "menu";
  private readonly world = new World();
  private readonly scoring = new Scoring();
  private readonly audio = new AudioManager();
  private readonly renderer: Renderer;
  private readonly timeline: TimelineRunner;
  private readonly loop: GameLoop;

  private readonly levels: Level[] = [
    buildLevel1(),
    buildLevel2(),
    buildLevel3(),
    buildLevel4(),
    buildLevel5(),
  ];
  private levelIdx = 0;
  /** Highest level index (0-based) reached so far — persisted for level select. */
  private unlockedLevel = 0;
  private runElapsed = 0;
  private outroT = 0;
  /** Counters + clock at the current level's start, for the per-level card. */
  private levelStart = { score: 0, kills: 0, hits: 0 };
  private levelStartTime = 0;
  private baseSeed = 0x5eed;
  private readonly shakeOut = { x: 0, y: 0 };

  private fpsEl?: HTMLDivElement;
  private fpsAccum = 0;
  private fpsFrames = 0;

  constructor(
    private readonly app: App,
    private readonly input: Input,
    private readonly ui: UI,
  ) {
    this.renderer = new Renderer(app.arena);
    this.timeline = new TimelineRunner((cue) => this.audio.cue(cue));
    this.loop = new GameLoop(
      (dt) => this.step(dt),
      (alpha, frameDt) => this.render(alpha, frameDt),
    );

    // Read deterministic seed / starting level from the URL (dev + sharing).
    const params = new URLSearchParams(location.search);
    const seed = params.get("seed");
    if (seed) this.baseSeed = hashSeed(seed);
    const lvl = params.get("level");
    if (lvl) this.levelIdx = Math.max(0, Math.min(this.levels.length - 1, Number(lvl) - 1));
    if (params.get("debug") === "1") this.initFps();

    // Restore how far the player has gotten, for the level-select screen.
    try {
      const saved = Number(localStorage.getItem(UNLOCK_STORAGE_KEY));
      if (Number.isFinite(saved)) {
        this.unlockedLevel = Math.max(0, Math.min(this.levels.length - 1, saved));
      }
    } catch {
      /* localStorage unavailable (private browsing, etc.) — start unlocked at level 1 */
    }
    this.ui.setLevels(this.levels.map((l) => ({ name: l.name, archetype: l.archetype })));
    this.ui.setUnlocked(this.unlockedLevel + 1);

    // World event reactions.
    this.world.onHit.on((e) => {
      this.scoring.hit();
      this.audio.sfxHit();
      navigator.vibrate?.(e.fatal ? [0, 60, 40, 80] : 35);
    });
    this.world.onGraze.on(() => {
      this.scoring.graze();
      this.audio.sfxGraze();
    });
    this.world.onHeal.on(() => {
      this.audio.sfxHeart();
      navigator.vibrate?.([0, 30, 20, 30]);
    });
    this.world.onEnemySpawn.on(() => this.audio.sfxEnemySpawn());
    this.world.onEnemyAttack.on((kind) => this.audio.sfxEnemyAttack(kind));
    this.world.onEnemyDeath.on((e) => {
      this.scoring.enemyKill(e.type);
      // The burning meteor gets its own fiery crackle instead of the generic thud.
      if (e.type === EN_METEOR) this.audio.sfxMeteorDeath();
      else this.audio.sfxEnemyDeath();
    });
    this.world.onSlash.on(({ hits, cut }) => {
      this.scoring.slash(hits, cut);
      this.audio.sfxSlash();
      if (hits > 0) this.audio.sfxSlashHit();
    });
    this.world.onExplosion.on(() => this.audio.sfxExplosion());
    this.world.onRoar.on(() => {
      this.audio.sfxRoar();
      navigator.vibrate?.(25);
    });
    this.world.onBoss.on((cmd) => {
      if (cmd === "enter") this.ui.banner("PYRE", "THE FINAL FLAME", 2600);
    });
    this.world.onBossDefeated.on(() => this.defeatBoss());

    // UI wiring.
    this.ui.onStart = () => void this.startRun();
    this.ui.onSelectLevel = (idx) => {
      this.levelIdx = Math.max(0, Math.min(this.unlockedLevel, idx));
      void this.startRun();
    };
    this.ui.onRetry = () => void this.startRun();
    this.ui.onNext = () => this.flowOn();
    this.ui.onPauseToggle = () => this.togglePause();
    this.ui.onExit = () => this.exitToMenu();
    this.ui.onAudioToggle = (on) => this.audio.setEnabled(on);

    // Portrait-only on phones: when a touch device rotates to landscape the
    // rotate-shield (index.html) covers the game — auto-pause so the run
    // isn't lost behind it. The player resumes after rotating back.
    const landscape = window.matchMedia("(orientation: landscape) and (pointer: coarse)");
    landscape.addEventListener?.("change", (e) => {
      if (e.matches && this.state === "playing") this.togglePause();
    });

    // Expose for headless testing / debugging.
    (window as unknown as { FLOWSTATE: unknown }).FLOWSTATE = {
      start: () => this.startRun(),
      state: () => this.state,
      snapshot: () => this.snapshot(),
      // Dev/test helpers: top up lives / swing the sword from headless runs.
      setLives: (n: number) => {
        this.world.player.lives = n;
      },
      slash: () => this.world.trySlash(),
    };
  }

  run(): void {
    this.audio.init().catch(() => {});
    this.ui.showTitle();
    this.loop.start();
  }

  private async startRun(): Promise<void> {
    await this.audio.resume();
    this.scoring.reset();
    this.runElapsed = 0;
    this.levelIdx = Math.min(this.levelIdx, this.levels.length - 1);
    this.loadLevel(this.levelIdx);
    this.ui.startGameUI();
    this.state = "playing";
  }

  private loadLevel(idx: number): void {
    this.levelIdx = idx;
    const level = this.levels[idx];
    this.world.reset(this.baseSeed + idx * 0x9e37);
    this.world.difficulty = LEVEL_DIFFICULTY[idx] ?? 1;
    this.timeline.load(level);
    this.audio.beginLevel(idx);
    this.levelStart = this.scoring.snapshot();
    this.levelStartTime = this.runElapsed;
    this.markReached(idx);
    if (idx === SWORD_UNLOCK_LEVEL) {
      this.ui.banner("SWORD UNLOCKED", "TAP TO SLASH", 1800);
    }
  }

  /** Unlock this level (and record it) the moment the player reaches it. */
  private markReached(idx: number): void {
    if (idx <= this.unlockedLevel) return;
    this.unlockedLevel = idx;
    this.ui.setUnlocked(this.unlockedLevel + 1);
    try {
      localStorage.setItem(UNLOCK_STORAGE_KEY, String(this.unlockedLevel));
    } catch {
      /* localStorage unavailable — unlock still applies for this session */
    }
  }

  private togglePause(): void {
    if (this.state === "playing") {
      this.state = "paused";
      this.ui.setPaused(true);
    } else if (this.state === "paused") {
      this.state = "playing";
      this.ui.setPaused(false);
    }
  }

  /** Bail out of the current run back to the title screen (from pause). */
  private exitToMenu(): void {
    this.state = "menu";
    this.audio.fadeOut(1.2);
    this.ui.setPaused(false);
    this.ui.showTitle();
  }

  // --- Simulation ----------------------------------------------------------
  private step(dt: number): void {
    if (this.state !== "playing" && this.state !== "outro") return;

    const s = this.input.state;
    this.world.setIntent({
      pointerActive: s.pointerActive,
      targetX: s.targetX,
      targetY: s.targetY,
      axisX: s.axisX,
      axisY: s.axisY,
    });
    // The sword is earned in Level 3 — taps before that just move the avatar.
    if (this.input.consumeTap() && this.levelIdx >= SWORD_UNLOCK_LEVEL) {
      this.world.trySlash();
    }

    this.world.step(dt);
    this.runElapsed += dt;

    if (this.state === "outro") {
      // Let the stage empty out, then hand over to the clear banner/results.
      this.outroT -= dt;
      if (this.outroT <= 0) this.onLevelClear();
      return;
    }

    this.timeline.step(dt, this.world);
    this.scoring.tick(dt);

    if (!this.world.player.alive) {
      this.endRun(false);
    } else if (this.timeline.finished) {
      this.beginOutro();
    }
  }

  /** The timeline is done: wind the stage down before declaring the clear. */
  private beginOutro(seconds = OUTRO_TIME): void {
    this.state = "outro";
    this.outroT = seconds;
    this.world.beginOutro();
    this.audio.cue("phase:clear");
    this.audio.sfxClear();
  }

  /** The dragon detonated — either the scripted finale or felled early by the
   * sword. A long, quiet wind-down lets the explosion fade and the empty
   * arena breathe before the results appear. */
  private defeatBoss(): void {
    if (this.state !== "playing") return;
    this.ui.banner("DRAGON DEFEATED", "THE FLAME IS OUT", 2600);
    this.beginOutro(BOSS_OUTRO_TIME);
  }

  private onLevelClear(): void {
    if (this.levelIdx + 1 < this.levels.length) {
      // The FLOW COMPLETE card: this level's own score, rank, and stats.
      // The player advances at their own pace via the FLOW ON button.
      this.state = "levelclear";
      const result = this.scoring.resultSince(
        this.levelStart,
        this.runElapsed - this.levelStartTime,
      );
      this.ui.showLevelClear(result, this.levels[this.levelIdx].name);
      navigator.vibrate?.([0, 30, 20, 40]);
    } else {
      this.endRun(true);
    }
  }

  /** FLOW ON pressed: advance to the next level with lives restored. */
  private flowOn(): void {
    if (this.state !== "levelclear") return;
    this.loadLevel(this.levelIdx + 1);
    // Checkpoint: clearing a phase refills the player to full lives (the
    // world reset in loadLevel starts the new level at full anyway — the
    // banner just says so).
    this.world.player.lives = PLAYER.startLives;
    const lvl = this.levels[this.levelIdx];
    if (this.levelIdx !== SWORD_UNLOCK_LEVEL) {
      this.ui.banner(lvl.name, "LIVES RESTORED", 1800);
    }
    this.ui.startGameUI();
    this.state = "playing";
  }

  private endRun(cleared: boolean): void {
    this.state = "results";
    this.audio.fadeOut(1.6);
    const result = this.scoring.result(this.runElapsed, cleared);
    const headline = cleared ? "RUN COMPLETE" : "FLOW BROKEN";
    this.ui.showResults(result, headline, this.timeline.progress);
    navigator.vibrate?.(cleared ? [0, 40, 30, 40, 30, 60] : 0);
  }

  // --- Render --------------------------------------------------------------
  private render(alpha: number, frameDt: number): void {
    // Hit-stop: briefly slow the simulation in realtime for impact.
    if (this.world.hitStop > 0) {
      this.world.hitStop -= frameDt;
      this.loop.timeScale = JUICE.hitStopScale;
    } else {
      this.loop.timeScale = 1;
    }

    const drawAlpha = this.state === "playing" ? alpha : 1;
    this.renderer.draw(this.world, drawAlpha, frameDt);
    // world.shake only decays while the sim is stepping (playing/outro); outside
    // that, freeze it at zero instead of re-jittering a stale, undecayed value
    // forever (e.g. on the results screen after a fatal hit).
    if (this.state === "playing" || this.state === "outro") {
      this.renderer.shakeOffset(this.world, this.shakeOut);
    } else {
      this.shakeOut.x = 0;
      this.shakeOut.y = 0;
    }
    this.app.applyShake(this.shakeOut.x, this.shakeOut.y);

    if (this.state === "playing" || this.state === "outro" || this.state === "paused") {
      const lvl = this.levels[this.levelIdx];
      this.ui.updateHud(
        lvl.name,
        this.scoring.score,
        this.scoring.multiplier,
        this.world.player.lives,
        PLAYER.startLives,
        this.timeline.progress,
      );
    }

    this.input.endFrame();
    if (this.fpsEl) this.updateFps(frameDt);
  }

  private snapshot() {
    return {
      state: this.state,
      level: this.levelIdx + 1,
      hazards: this.world.hazards.count,
      enemies: this.world.enemies.count,
      hearts: this.world.hearts.count,
      boss: this.world.boss.active,
      particles: this.world.particles.count,
      lives: this.world.player.lives,
      score: Math.floor(this.scoring.score),
      grazes: this.scoring.grazes,
      progress: Number(this.timeline.progress.toFixed(3)),
      player: { x: Math.round(this.world.player.x), y: Math.round(this.world.player.y) },
    };
  }

  private initFps(): void {
    this.fpsEl = document.createElement("div");
    this.fpsEl.style.cssText =
      "position:fixed;top:6px;left:8px;z-index:99;font:12px monospace;color:#36f9d0;pointer-events:none;text-shadow:0 0 4px #000;";
    document.body.appendChild(this.fpsEl);
  }

  private updateFps(frameDt: number): void {
    this.fpsAccum += frameDt;
    this.fpsFrames++;
    if (this.fpsAccum >= 0.5 && this.fpsEl) {
      const fps = Math.round(this.fpsFrames / this.fpsAccum);
      this.fpsEl.textContent = `${fps} fps · hz:${this.world.hazards.count} en:${this.world.enemies.count} px:${this.world.particles.count}`;
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }
  }
}
